import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AssistService } from '../../../../service/assist-service/assist.service';
import { PatientService } from '../../../../service/patient-service/patient.service';
import { decryptField } from '../../../../utils/decrypt-utils';
import * as moment from 'moment-timezone';

@Component({
  selector: 'app-assist-detail-modal',
  templateUrl: './assist-detail-modal.component.html',
  styleUrls: ['./assist-detail-modal.component.css']
})
export class AssistDetailModalComponent implements OnInit, OnDestroy {
  @Input() referenceId: string;
  @Output() onCloseModal = new EventEmitter<void>();

  assist: any = null;
  isLoading: boolean = true;
  errorMessage: string = '';

  broadcastSummary: any = {};
  patientInfo: any = {};
  proposals: any[] = [];
  selectedProposalId: number | string = null;

  levelOfCareMap: { [key: number]: string } = {
    1: 'Assisted Living',
    2: 'Board & Care',
    3: 'Memory Care',
    4: 'Independent Living',
    5: 'Skilled Nursing'
  };

  specialCareMap: { [key: number]: string } = {
    1: 'Oxygen',
    2: 'Catheter',
    3: 'Catheter (Self)',
    4: 'Bedsore',
    5: 'Colostomy',
    6: 'Colostomy (Self)'
  };

  dietRestrictionMap: { [key: number]: string } = {
    1: 'Diabetic',
    2: 'Low Salt',
    3: 'Renal',
    4: 'Puree'
  };

  entitlementMap: { [key: number]: string } = {
    1: 'Medi-Cal',
    2: 'ALWPP',
    3: 'Medi-Cal Pending',
    4: 'VA'
  };

  preIntakeData: any = null;

  constructor(
    private assistService: AssistService,
    private patientService: PatientService
  ) { }

  ngOnInit(): void {
    if (this.referenceId) {
      this.fetchAssistDetail();
    }
  }

  ngOnDestroy(): void { }

  closeModal(): void {
    this.onCloseModal.emit();
  }

  fetchAssistDetail(): void {
    this.isLoading = true;
    this.errorMessage = '';
    forkJoin([
      this.patientService.getPreIntakeData(),
      this.assistService.getAssistDetail(this.referenceId)
    ]).pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        ([preIntake, res]) => {
          this.isLoading = false;
          this.preIntakeData = preIntake;
          const data = (res && res.data) ? res.data : (res && res.assist ? res.assist : res);
          this.assist = data;
          this.processAssistData(data);
        },
        (error) => {
          console.error('Error fetching assist details:', error);
          this.isLoading = false;
          this.errorMessage = 'Failed to load assist details.';
        }
      );
  }

  processAssistData(data: any): void {
    if (!data) return;

    // 1. Intake Broadcast Details
    const statusNum = typeof data.status === 'string' ? parseInt(data.status, 10) : Number(data.status);
    let statusLabel = '—';
    let statusStyle = { 'background-color': '#f3f4f6', 'color': '#374151' };

    switch (statusNum) {
      case 0:
        statusLabel = 'Available';
        statusStyle = { 'background-color': '#2dce89', 'color': '#ffffff' };
        break;
      case 1:
        statusLabel = 'Accepted';
        statusStyle = { 'background-color': '#0d6efd', 'color': '#ffffff' };
        break;
      case 2:
        statusLabel = 'Closed';
        statusStyle = { 'background-color': '#dc3545', 'color': '#ffffff' };
        break;
      default:
        statusLabel = '—';
        statusStyle = { 'background-color': '#f3f4f6', 'color': '#6b7280' };
        break;
    }

    const accType = data.e_accommodation_type || data.e_accommodations || data.e_accommodation_types || data.e_accommodationtypes || {};
    const accommodationName = accType.name || data.accommodation_type || '—';

    // Preferred Location mapping
    let preferredLocStr = '—';
    if (data.preferred_location && Array.isArray(data.preferred_location) && data.preferred_location.length) {
      const loc = data.preferred_location[0];
      preferredLocStr = [loc.city_name, loc.state_name].filter(Boolean).join(', ');
    } else {
      const cityName = (data.e_cities && data.e_cities.name) || data.city_name || '';
      const stateName = (data.e_states && data.e_states.name) || data.state_name || '';
      preferredLocStr = [cityName, stateName].filter(Boolean).join(', ') || '—';
    }

    // Budget range formatting
    let budgetStr = '—';
    if (data.budget_min !== undefined && data.budget_max !== undefined && data.budget_min !== null && data.budget_max !== null) {
      const minStr = isNaN(Number(data.budget_min)) ? data.budget_min : `$${Number(data.budget_min).toLocaleString()}`;
      const maxStr = isNaN(Number(data.budget_max)) ? data.budget_max : `$${Number(data.budget_max).toLocaleString()}`;
      budgetStr = `${minStr} - ${maxStr}` + (data.budgettype_id ? ` / ${data.budgettype_id}` : '');
    }

    // Dates & Length of stay
    const createdDate = data.created_date ? (moment(data.created_date).isValid() ? moment(data.created_date).format('MM/DD/YYYY') : data.created_date) : '—';
    const onboardingDate = data.onboarding_date ? (moment(data.onboarding_date).isValid() ? moment(data.onboarding_date).format('MM/DD/YYYY') : data.onboarding_date) : '—';
    const departureDate = data.departure_date ? (moment(data.departure_date).isValid() ? moment(data.departure_date).format('MM/DD/YYYY') : data.departure_date) : '—';
    const walkthroughDate = data.walkthrough_date ? (moment(data.walkthrough_date).isValid() ? moment(data.walkthrough_date).tz("America/Los_Angeles").format('MM/DD/YYYY HHmm') : data.walkthrough_date) : '—';

    let lengthOfStayStr = '—';
    if (data.onboarding_date && data.departure_date && moment(data.onboarding_date).isValid() && moment(data.departure_date).isValid()) {
      const nights = moment(data.departure_date).startOf('day').diff(moment(data.onboarding_date).startOf('day'), 'days') + 1;
      if (nights >= 0) {
        lengthOfStayStr = `${nights} nights`;
      }
    }

    this.broadcastSummary = {
      statusLabel: statusLabel,
      statusStyle: statusStyle,
      referenceNo: data.reference_id || data.reference_no || '—',
      createdDate: createdDate,
      accommodation: accommodationName,
      preferredLocation: preferredLocStr,
      budget: budgetStr,
      onboardingDate: onboardingDate,
      departureDate: departureDate,
      lengthOfStay: lengthOfStayStr,
      walkthroughDate: walkthroughDate
    };

    // 2. Patient Information (Source: assist.e_patients or assist.e_intakes.e_patients)
    const intake = (data && data.e_intakes) ? (Array.isArray(data.e_intakes) ? data.e_intakes[0] : data.e_intakes) : {};
    const patient = (data && data.e_patients)
      ? (Array.isArray(data.e_patients) ? data.e_patients[0] : data.e_patients)
      : ((intake && intake.e_patients) ? (Array.isArray(intake.e_patients) ? intake.e_patients[0] : intake.e_patients) : (data.patient || {}));

    const firstName = decryptField(patient.first_name || (intake && intake.first_name) || data.first_name);
    const lastName = decryptField(patient.last_name || (intake && intake.last_name) || data.last_name);
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    const phone = decryptField(patient.phone || (intake && intake.phone) || data.phone);
    const rawDob = patient.date_of_birth || patient.dob || (intake && intake.date_of_birth) || data.date_of_birth;
    const decryptedDob = rawDob ? decryptField(rawDob) : null;
    const formattedDob = decryptedDob && moment(decryptedDob).isValid() ? moment(decryptedDob).format('MM/DD/YYYY') : (decryptedDob || '—');
    const age = decryptedDob && moment(decryptedDob).isValid() ? moment().diff(moment(decryptedDob), 'years') : null;

    const rawIncomeVal = intake.annual_income || patient.annual_income || data.annual_income;
    const rawIncome = rawIncomeVal ? decryptField(rawIncomeVal) : null;
    const formattedIncome = rawIncome ? (isNaN(Number(rawIncome)) ? rawIncome : `$${Number(rawIncome).toLocaleString()}`) : '—';

    // Relationship arrays mapping
    const specialCareNeedsStr = this.mapListItems(intake.e_patientintakespecialcare || data.e_patientintakespecialcare, 'intakespecialcareneeds_id', this.specialCareMap);
    const dietRestrictionsStr = this.mapListItems(intake.e_patientintakedietrestriction || data.e_patientintakedietrestriction, 'intakedietrestrictions_id', this.dietRestrictionMap);
    const entitlementsStr = this.mapListItems(intake.e_patientintakeentitlement || data.e_patientintakeentitlement, 'intakeentitlements_id', this.entitlementMap);

    const levelOfCare = (intake.e_levelofcare && intake.e_levelofcare.name)
      || (data.e_levelofcare && data.e_levelofcare.name)
      || this.levelOfCareMap[intake.levelofcare_id]
      || this.levelOfCareMap[data.levelofcare_id]
      || '—';

    const rawDiagnosis = decryptField(intake.diagnosis || patient.diagnosis || data.diagnosis);
    const diagnosisStr = rawDiagnosis || (intake.e_primarydiagnosis && intake.e_primarydiagnosis.name) || '—';
    const medicationNotes = decryptField(intake.medication_notes || patient.medication_notes || data.medication_notes) || '—';
    const additionalComments = decryptField(intake.additional_comments || patient.additional_comments || data.additional_comments) || '—';

    let heightStr = '—';
    const hFt = intake.height_feet !== undefined && intake.height_feet !== null ? intake.height_feet : (patient.height_feet !== undefined && patient.height_feet !== null ? patient.height_feet : data.height_feet);
    const hIn = intake.height_inches !== undefined && intake.height_inches !== null ? intake.height_inches : (patient.height_inches !== undefined && patient.height_inches !== null ? patient.height_inches : data.height_inches);
    if ((hFt !== undefined && hFt !== null) || (hIn !== undefined && hIn !== null)) {
      if (Number(hFt) > 0 || Number(hIn) > 0) {
        heightStr = `${hFt || 0}' ${hIn || 0}"`;
      }
    } else {
      const singleHeight = patient.height || intake.height || data.height;
      if (singleHeight) {
        heightStr = String(singleHeight);
      }
    }

    this.patientInfo = {
      name: fullName || '—',
      phone: phone || '—',
      email: patient.email || (intake && intake.email) || data.email || '—',
      levelOfCare: levelOfCare,
      dateOfBirth: formattedDob,
      age: age !== null ? `${age} years` : '—',
      annualIncome: formattedIncome,
      height: heightStr,
      weight: (patient.weight || intake.weight || data.weight) ? String(patient.weight || intake.weight || data.weight) : '—',
      ambulation: this.getLookupName('ambulation', intake.ambulation || patient.ambulation || data.ambulation),
      transfers: this.getLookupName('transfers', intake.transfers || patient.transfers || data.transfers),
      bathing: this.getLookupName('bathing', intake.bathing || patient.bathing || data.bathing),
      eating: this.getLookupName('eating', intake.eating || patient.eating || data.eating),
      mentalStatus: this.getLookupName('mentalStatus', intake.mental_status || patient.mental_status || data.mental_status),
      incontinenceBowel: this.getLookupName('incontinencecare', intake.incontinence_care_bowel || intake.incontinence_bowel || patient.incontinence_bowel),
      incontinenceBladder: this.getLookupName('incontinencecare', intake.incontinence_care_bladder || intake.incontinence_bladder || patient.incontinence_bladder),
      diabetesMedication: this.getLookupName('diabetes', intake.diabetes || intake.diabetes_medication || patient.diabetes),
      dressing: this.getLookupName('dressing', intake.dressing || patient.dressing || data.dressing),
      hospiceImminent: this.capitalize(intake.hospice_imminent || patient.hospice_imminent || data.hospice_imminent),
      specialCareNeeds: specialCareNeedsStr,
      dietRestrictions: dietRestrictionsStr,
      possibleEntitlements: entitlementsStr,
      primaryDiagnosis: diagnosisStr,
      medicationNotes: medicationNotes || '—',
      additionalComments: additionalComments || '—'
    };

    // 3. Proposals (Source: assist.e_agentproposals[])
    const rawProposals = (data && data.e_agentproposals && Array.isArray(data.e_agentproposals)) ? data.e_agentproposals : [];

    this.proposals = rawProposals.map((proposal: any, index: number) => {
      const agentObj = proposal.e_placementagents || proposal.placementagent || {};
      const agentFirstName = agentObj.first_name || '';
      const agentLastName = agentObj.last_name || '';
      const agentFullName = [agentFirstName, agentLastName].filter(Boolean).join(' ') || '—';

      let statusLabel = '—';
      let statusStyle = { 'background-color': '#f3f4f6', 'color': '#374151' };

      switch (proposal.status) {
        case 0:
          statusLabel = 'Draft';
          statusStyle = { 'background-color': '#16a7d1', 'color': '#ffffff' };
          break;
        case 1:
          statusLabel = 'Sent';
          statusStyle = { 'background-color': '#2dce89', 'color': '#ffffff' };
          break;
        case 2:
          statusLabel = 'Accepted';
          statusStyle = { 'background-color': '#4d94dd', 'color': '#ffffff' };
          break;
        default:
          statusLabel = '—';
          statusStyle = { 'background-color': '#f3f4f6', 'color': '#6b7280' };
          break;
      }

      return {
        id: index + 1,
        date: createdDate,
        reference: data.reference_id || data.reference_no || '—',
        agent: agentFullName,
        status_label: statusLabel,
        status_style: statusStyle,
        raw_proposal: proposal
      };
    });
  }

  private mapListItems(itemsArr: any[], idKey: string, mapObj: { [key: number]: string }): string {
    if (!itemsArr || !Array.isArray(itemsArr) || !itemsArr.length) return '—';
    const mapped = itemsArr.map(item => {
      if (!item) return '';
      return mapObj[item[idKey]] || item.name || item.label || '';
    }).filter(Boolean);
    return mapped.length ? mapped.join(', ') : '—';
  }

  private getLookupName(listKey: string, val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    if (this.preIntakeData && this.preIntakeData[listKey] && Array.isArray(this.preIntakeData[listKey])) {
      const option = this.preIntakeData[listKey].find(opt => Number(opt.id) === Number(val));
      if (option && option.name) return option.name;
    }
    return this.capitalize(val);
  }

  private capitalize(val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    if (typeof val === 'number') return String(val);
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      const label = val.name || val.label || val.title || val.value;
      if (!label) return '—';
      const strLabel = String(label);
      return strLabel.charAt(0).toUpperCase() + strLabel.slice(1).toLowerCase();
    }
    const str = String(val);
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  viewProposal(proposal: any): void {
    if (proposal && proposal.raw_proposal && proposal.raw_proposal.id) {
      this.selectedProposalId = proposal.raw_proposal.id;
    }
  }

  closeProposalModal(): void {
    this.selectedProposalId = null;
  }
}
